import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Spinner, Alert, Chip, Tabs, EmptyState, RadioGroup } from '@glplus/glpweb-dsl';
import {
  adminApiClient,
  AdminApiError,
  type ReviewProduct,
  type VendorQuestionDecision,
  type VendorQuestionItem,
  type VendorQuestionState,
} from '../../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../../lib/buttonStyles';
import { Actions, BORDER, Card, Field, HEADING, MUTED, Panel, Photo, PRIMARY_BTN, SideBySide, day, money } from './parts';

/**
 * Products a vendor handed to us because nobody could say what they are
 * (§15q). We propose one product — never a list — say none fit, or say we are
 * stuck. A proposal goes back to the vendor as a yes/no; we do not attach a
 * product on our own say-so.
 */

const WHY: Record<string, string> = {
  barcode_name_differs: 'The barcode the vendor sent belongs to the product on the right, but the two names share nothing.',
  name_similar: 'The vendor sent no barcode, and the product on the right has nearly the same name.',
  staff_proposal: 'We proposed the product on the right.',
};

const BESIDE: Record<ReviewProduct['barcodeBeside'], string> = {
  same: 'the same as the vendor’s',
  different: 'different from the vendor’s',
  none: 'no barcode',
};

export function barcodeLine(p: ReviewProduct): string {
  return p.barcodeLast4 ? `${p.barcodeLast4} — ${BESIDE[p.barcodeBeside]}` : 'No barcode';
}

function outcomeMessage(kind: 'propose' | 'create' | 'stuck', vendor: string, r: VendorQuestionDecision): string {
  if (kind === 'propose') return `Sent to ${vendor} as a yes or no.`;
  if (kind === 'stuck') return `Marked stuck. ${vendor} sees your reason and can send more.`;
  if (r.blockState === null) return `Created as ${vendor}’s own product. It is live.`;
  return `Created as ${vendor}’s own product. It stays hidden until they tell us: ${r.missing.map((m) => m.replace(/_/g, ' ')).join(', ')}.`;
}

export function QuestionsTab({ canReview, onCount }: { canReview: boolean; onCount: (n: number) => void }) {
  const [state, setState] = useState<VendorQuestionState>('with_us');
  const [items, setItems] = useState<VendorQuestionItem[] | null>(null);
  const [counts, setCounts] = useState<Record<VendorQuestionState, number>>({ with_us: 0, proposed: 0, stuck: 0 });
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminApiClient.listVendorQuestions(state);
      setItems(res.items);
      setCounts(res.counts);
      onCount(res.counts.with_us);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    }
  }, [state, onCount]);

  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Tabs
        ariaLabel="Which products"
        value={state}
        onChange={(_e, v) => setState(v as VendorQuestionState)}
        tabs={[
          { value: 'with_us', label: 'With us', badgeCount: counts.with_us },
          { value: 'proposed', label: 'Waiting on the vendor', badgeCount: counts.proposed },
          { value: 'stuck', label: 'Stuck', badgeCount: counts.stuck },
        ]}
      />
      {flash && <Alert severity="success">{flash}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {!items && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      )}
      {items && items.length === 0 && (
        <EmptyState
          icon="listAlt"
          title={state === 'with_us' ? 'Nothing is waiting on us' : state === 'proposed' ? 'No proposals are waiting on a vendor' : 'Nothing is stuck'}
          body={state === 'with_us' ? 'A product a vendor asks us to sort out shows here.' : undefined}
        />
      )}
      {items?.map((q) => (
        <QuestionCard
          key={q.questionId}
          q={q}
          canReview={canReview && q.state === 'with_us'}
          onDone={(msg) => {
            setFlash(msg);
            void load();
          }}
        />
      ))}
    </Box>
  );
}

function QuestionCard({ q, canReview, onDone }: { q: VendorQuestionItem; canReview: boolean; onDone: (msg: string) => void }) {
  const [open, setOpen] = useState<'propose' | 'create' | 'stuck' | null>(null);
  const sent = q.sent;
  const vendor = q.vendor.businessName;
  const toggle = (k: 'propose' | 'create' | 'stuck') => setOpen(open === k ? null : k);

  return (
    <Card testId={`vendor-question-${q.questionId}`}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>
          {sent?.name ?? 'Unnamed product'}
        </Typography>
        <Chip label={vendor} size="small" />
        {q.state === 'stuck' && <Chip label="Stuck" size="small" color="error" />}
        {q.state === 'proposed' && <Chip label="Waiting on the vendor" size="small" color="warning" />}
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>
        Added {day(q.askedAt)}
        {q.handedAt ? ` · with us since ${day(q.handedAt)}` : ''}
      </Typography>
      <Typography sx={{ mt: 1, fontSize: 14 }} data-testid="vendor-question-why">
        {WHY[q.reason] ?? ''}
        {q.vendorAnswer === 'no' ? ' The vendor says it is not that product.' : q.state === 'with_us' ? ' The vendor asked us to sort it out.' : ''}
      </Typography>
      {q.state === 'stuck' && q.stuckReason && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="warning">We told the vendor: “{q.stuckReason}”</Alert>
        </Box>
      )}

      <SideBySide
        leftTitle="What the vendor sent"
        rightTitle={q.reason === 'staff_proposal' ? 'What we proposed' : 'Our first guess'}
        left={
          sent ? (
            <>
              {sent.photoUrl && (
                <Box sx={{ mb: 1.5 }}>
                  <Photo src={sent.photoUrl} alt={`The vendor’s photo of ${sent.name ?? 'the product'}`} />
                </Box>
              )}
              <Field label="Name">{sent.name}</Field>
              <Field label="Size">{sent.size}</Field>
              <Field label="Department">{[sent.department, sent.category].filter(Boolean).join(' › ')}</Field>
              <Field label="Barcode" testId="vendor-question-sent-barcode">
                {sent.barcode ?? (sent.noBarcode ? 'The vendor says it has none' : null)}
              </Field>
              <Field label="Their SKU">{sent.vendorSku}</Field>
              <Field label="Price">{money(sent.priceCents)}</Field>
              <Field label="Description">{sent.descriptionShort}</Field>
              <Field label="Ingredients">{sent.ingredients}</Field>
              <Field label="How it arrived">{sent.source === 'manual' ? 'Typed in' : `From a file${sent.file ? ` — ${sent.file}` : ''}`}</Field>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: MUTED }}>We no longer have what the vendor sent.</Typography>
          )
        }
        right={<ProductFacts p={q.current} />}
      />

      {q.reopened.length > 0 && (
        <Panel title="What the vendor added since">
          {q.reopened.map((r) => (
            <Box key={r.at} sx={{ mb: 1 }} data-testid="vendor-question-reopened">
              <Typography sx={{ fontSize: 13, color: MUTED }}>{day(r.at)}</Typography>
              {r.barcode && <Typography sx={{ fontSize: 14 }}>Barcode: {r.barcode === 'none' ? 'it has none' : r.barcode}</Typography>}
              {r.note && <Typography sx={{ fontSize: 14 }}>“{r.note}”</Typography>}
              {r.photoUrl && (
                <Box sx={{ mt: 1 }}>
                  <Photo src={r.photoUrl} alt="Photo the vendor added" size={160} />
                </Box>
              )}
            </Box>
          ))}
        </Panel>
      )}

      {q.staff.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>What we have done</Typography>
          {q.staff.map((s) => (
            <Typography key={s.at} sx={{ fontSize: 13, color: HEADING }}>
              {day(s.at)} — {s.action === 'propose' ? `proposed ${s.glpProductId ?? 'a product'}` : s.action === 'create_new' ? 'made it their own product' : 'marked it stuck'}
              {s.note ? ` — “${s.note}”` : ''}
            </Typography>
          ))}
        </Box>
      )}

      {canReview && (
        <Actions>
          <Button ariaLabel="Propose a product" buttonText="Propose a product" variant="contained" size="small" sx={PRIMARY_BTN} dataTestId="vendor-question-propose" onClick={() => toggle('propose')} />
          <Button ariaLabel="None of ours fit" buttonText="None of ours fit" variant="outlined" size="small" sx={NEUTRAL_BTN} dataTestId="vendor-question-create" onClick={() => toggle('create')} />
          <Button ariaLabel="We are stuck" buttonText="We’re stuck" variant="outlined" size="small" sx={DANGER_BTN} dataTestId="vendor-question-stuck" onClick={() => toggle('stuck')} />
        </Actions>
      )}
      {open === 'propose' && <ProposePanel q={q} onDone={(r) => onDone(outcomeMessage('propose', vendor, r))} />}
      {open === 'create' && <CreatePanel q={q} onDone={(r) => onDone(outcomeMessage('create', vendor, r))} />}
      {open === 'stuck' && <StuckPanel q={q} onDone={(r) => onDone(outcomeMessage('stuck', vendor, r))} />}
    </Card>
  );
}

function ProductFacts({ p }: { p: ReviewProduct }) {
  return (
    <>
      <Box sx={{ mb: 1.5 }}>
        <Photo src={p.imageUrl} alt={p.name} />
      </Box>
      <Field label="Name">{p.name}</Field>
      <Field label="Brand">{p.brand}</Field>
      <Field label="Size">{p.size}</Field>
      <Field label="Department">{[p.department, p.category].filter(Boolean).join(' › ')}</Field>
      <Field label="Barcode" testId="vendor-question-our-barcode">
        {barcodeLine(p)}
      </Field>
      <Field label="Product id">{p.glpProductId}</Field>
      <Field label="Ingredients">{p.ingredients}</Field>
    </>
  );
}

function useDecision(onDone: (r: VendorQuestionDecision) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (work: () => Promise<VendorQuestionDecision>) => {
    setBusy(true);
    setError(null);
    try {
      onDone(await work());
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function ProposePanel({ q, onDone }: { q: VendorQuestionItem; onDone: (r: VendorQuestionDecision) => void }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ReviewProduct[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<ReviewProduct | null>(null);
  const [note, setNote] = useState('');
  const { busy, error, run } = useDecision(onDone);

  const search = async () => {
    if (term.trim().length < 2) return;
    setSearching(true);
    setSearchError(null);
    setChosen(null);
    try {
      setResults((await adminApiClient.searchReviewProducts(term.trim())).items);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Panel title="Which product do we think it is?">
      <Box
        component="form"
        role="search"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          void search();
        }}
        sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <TextField label="Name, product id, or a barcode" ariaLabel="Search for a product" value={term} onChange={(e) => setTerm(e.target.value)} dataTestId="vendor-question-search" />
        </Box>
        <Button ariaLabel="Search products" buttonText={searching ? 'Searching…' : 'Search'} type="submit" variant="outlined" size="small" sx={NEUTRAL_BTN} disabled={searching || term.trim().length < 2} dataTestId="vendor-question-search-go" />
      </Box>
      {searchError && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{searchError}</Alert>
        </Box>
      )}
      {results && results.length === 0 && <Typography sx={{ mt: 1.5, fontSize: 14, color: MUTED }}>Nothing found. Try fewer words.</Typography>}
      {results && results.length > 0 && (
        <Box sx={{ mt: 1.5, border: `1px solid ${BORDER}`, borderRadius: '8px', backgroundColor: '#fff' }}>
          {results.map((p) => (
            <Box
              key={p.glpProductId}
              data-testid={`vendor-question-result-${p.glpProductId}`}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.25, borderBottom: `1px solid ${BORDER}`, '&:last-of-type': { borderBottom: 'none' }, backgroundColor: chosen?.glpProductId === p.glpProductId ? '#FFF3E8' : 'transparent' }}
            >
              <Photo src={p.imageUrl} alt={p.name} size={48} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{[p.brand, p.name].filter(Boolean).join(' — ')}</Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {[p.size, [p.department, p.category].filter(Boolean).join(' › '), barcodeLine(p), p.glpProductId].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Button ariaLabel={`Choose ${p.name}`} buttonText={chosen?.glpProductId === p.glpProductId ? 'Chosen' : 'Choose'} variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={() => setChosen(p)} />
            </Box>
          ))}
        </Box>
      )}
      {chosen && (
        <Box sx={{ mt: 2 }}>
          <TextField label={`A word to ${q.vendor.businessName} (optional)`} ariaLabel="Note to the vendor" value={note} onChange={(e) => setNote(e.target.value)} dataTestId="vendor-question-note" />
          <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
            They will be asked “Is this {[chosen.brand, chosen.name].filter(Boolean).join(' ')}?” — yes makes it live, no brings it back to us.
          </Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button
          ariaLabel="Send the proposal to the vendor"
          buttonText={busy ? 'Sending…' : 'Send to the vendor'}
          variant="contained"
          size="small"
          sx={PRIMARY_BTN}
          disabled={busy || !chosen}
          dataTestId="vendor-question-send"
          onClick={() => chosen && void run(() => adminApiClient.proposeVendorMatch(q.questionId, { glpProductId: chosen.glpProductId, ...(note.trim() ? { note: note.trim() } : {}) }))}
        />
      </Box>
    </Panel>
  );
}

/** The barcode most likely to be the product's own: what they added last, else what they first sent — unless ours already has it. */
export function likelyBarcode(q: Pick<VendorQuestionItem, 'reopened' | 'sent' | 'current'>): string {
  const added = [...q.reopened].reverse().find((r) => r.barcode);
  if (added?.barcode) return added.barcode === 'none' ? '' : added.barcode;
  if (q.sent?.barcode && q.current.barcodeBeside !== 'same') return q.sent.barcode;
  return '';
}

function CreatePanel({ q, onDone }: { q: VendorQuestionItem; onDone: (r: VendorQuestionDecision) => void }) {
  const guess = likelyBarcode(q);
  const [kind, setKind] = useState<'none' | 'value'>(guess ? 'value' : 'none');
  const [barcode, setBarcode] = useState(guess);
  const { busy, error, run } = useDecision(onDone);
  return (
    <Panel title={`Make it ${q.vendor.businessName}’s own product`}>
      <Typography sx={{ fontSize: 14, mb: 1 }}>
        A new product is created from what the vendor sent. It goes live if nothing is missing; otherwise the vendor is asked for the rest.
      </Typography>
      <RadioGroup
        ariaLabel="Does it have a barcode?"
        name={`barcode-${q.questionId}`}
        value={kind}
        onChange={(e) => setKind(e.target.value as 'none' | 'value')}
        options={[
          { value: 'none', label: 'It has no barcode — assign one' },
          { value: 'value', label: 'It has this barcode' },
        ]}
      />
      {kind === 'value' && (
        <Box sx={{ mt: 1, maxWidth: 320 }}>
          <TextField label="Barcode" ariaLabel="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} dataTestId="vendor-question-barcode" />
        </Box>
      )}
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button
          ariaLabel="Create the vendor’s own product"
          buttonText={busy ? 'Creating…' : 'Create'}
          variant="contained"
          size="small"
          sx={PRIMARY_BTN}
          disabled={busy || (kind === 'value' && !barcode.trim())}
          dataTestId="vendor-question-create-go"
          onClick={() => void run(() => adminApiClient.createVendorOwnProduct(q.questionId, kind === 'none' ? { kind: 'none' } : { kind: 'value', value: barcode.trim() }))}
        />
      </Box>
    </Panel>
  );
}

function StuckPanel({ q, onDone }: { q: VendorQuestionItem; onDone: (r: VendorQuestionDecision) => void }) {
  const [reason, setReason] = useState('');
  const { busy, error, run } = useDecision(onDone);
  return (
    <Panel title="Tell the vendor what we need">
      <TextField label={`${q.vendor.businessName} reads this, word for word`} ariaLabel="Why we are stuck" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} dataTestId="vendor-question-reason" />
      <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>The product stays hidden. The vendor can send a barcode, a photo or a note, and it comes back to us.</Typography>
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button ariaLabel="Mark the product stuck" buttonText={busy ? 'Saving…' : 'Mark stuck'} variant="contained" size="small" sx={PRIMARY_BTN} disabled={busy || !reason.trim()} dataTestId="vendor-question-stuck-go" onClick={() => void run(() => adminApiClient.cantIdentifyVendorProduct(q.questionId, reason.trim()))} />
      </Box>
    </Panel>
  );
}
